'use client';

import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { useLocale, useTranslations } from 'next-intl';
import { LocalizedField } from '@/types/amplience';
import { isCurrentPromoActivatedAtom, promoConfigAtom } from '@/atoms/promoAtom';
import { Locale, getLanguage } from '@/utility/locale';
import { tracker } from '@/utility/tracking/tracker';
import { ResponsiveDialog } from '../ResponsiveDialog/ResponsiveDialog';

// const IMAGE_WIDTHS = [400, 500, 600, 800];

interface PromoPopupProps {
  open: boolean;
  onClose: () => void;
  applyVoucher: () => void;
  nested?: boolean;
}

export const getLocalizedValue = (field: LocalizedField, locale: Locale): string => {
  const exactMatch = field.values.find((v) => v.locale === locale);
  const fallbackMatch = field.values.find((v) =>
    v.locale.startsWith(locale ? getLanguage(locale) : '')
  );
  return exactMatch?.value || fallbackMatch?.value || '';
};

const PromoPopup = ({ open, onClose, applyVoucher, nested = false }: PromoPopupProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const promoSlot = useAtomValue(promoConfigAtom);
  const isActivated = useAtomValue(isCurrentPromoActivatedAtom);

  const content = promoSlot!.content['promo-tool'];

  const title = getLocalizedValue(content.campaignDetails.title, locale);
  const description = getLocalizedValue(content.campaignDetails.description, locale);
  const voucherCode = content.campaignDetails.code;

  const handleApplyVoucher = () => {
    applyVoucher();
    tracker.trackFeature({
      featureUsed: 'content-popup-activated',
      detail: {},
      promocode: voucherCode,
    });
    setTimeout(() => onClose(), 300);
  };

  return (
    <ResponsiveDialog
      dismissible
      className={clsx('pb-8 md:max-w-1/2 md:pb-0', { 'z-150': nested })}
      open={open}
      title={t('promo.codeLabel')}
      closeLabel={t('general.close')}
      onOpenChange={onClose}
      nested={nested}
    >
      {/* DEV-222893 - remove weird promo image for now */}
      {/* {content.backgroundImage && (
          <div className="max-h-[40vh] rounded-t-2xl md:h-auto md:max-h-none md:w-1/2 md:rounded-t-none md:rounded-l-xl">
            <AmplienceResponsiveImage
              source={{
                src: content.backgroundImage.image,
                widths: IMAGE_WIDTHS,
                sizes: '(min-width: 768px) 50vw, 100vw',
              }}
              alt={content.backgroundImage.altText ?? ''}
            />
          </div>
        )} */}
      <div className="w-full flex-col p-4 py-0 md:flex md:p-6 md:py-6">
        <h2 className="px-4 pb-6 text-center text-xl font-bold uppercase sm:text-2xl md:px-6 md:pt-12 md:pb-6 md:text-[1.75rem] lg:text-[2rem]">
          {title}
        </h2>
        {voucherCode && (
          <div className="flex flex-col items-center justify-center gap-4 md:gap-6 md:py-6">
            <h3 className="text-base font-bold md:text-lg">
              {t('promo.codeLabel')}: {voucherCode}
            </h3>
            {isActivated ? (
              <span className="rounded bg-green-100 px-4 py-2 text-green-700">
                {t('promo.codeActivated')}
              </span>
            ) : (
              <button
                onClick={handleApplyVoucher}
                className="bg-black px-4 py-2 text-white hover:bg-gray-800 disabled:opacity-60"
              >
                {t('promo.applyButton.default')}
              </button>
            )}
          </div>
        )}
        <p className="pt-6 pb-12 text-center text-xs md:px-6 md:pt-12 md:pb-6 md:text-sm">
          {description}
        </p>
      </div>
    </ResponsiveDialog>
  );
};

export default PromoPopup;
